import React, { useCallback, useEffect, useState } from 'react';
import UserNote from './UserNote';
import styled from "styled-components/macro";
import UserNoteForm from "./UserNoteForm";
import { ErrorMessage, mixins } from "./styles";
import Auth from "../../Auth";
import { SudoButton } from "src/sudokit/components/SudoButton";

export const UserNotesWrapper = styled.div`
  ${mixins.flexColumn}

  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 10px;
`;

export const UserNotesList = styled.div`
  ${mixins.flexColumn}

  gap: 8px;

  max-height: 350px;
  overflow-y: auto;
`;

export const UserNotesSectionHeader = styled.div`
  ${mixins.flexRow}

  justify-content: space-between;
  align-items: center;

  button#add-note {
    background: unset;
    color: black;
    margin: 0;
    border: 1px solid black;
    padding: 4px;
    border-radius: 0;
  }
`;

const UserNotes = ({ user, userCache }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [notes, setNotes] = useState([]);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const { userNotes } = await Auth.fetchWithIdToken(`/api/admin/userNotes/${user.uid}/list`);
      setNotes(userNotes);
      const userIds = userNotes.map(note => note.createdBy);
      await userCache.fetchIds(userIds);
      setError(null);
    } catch (error) {
      console.error(error);
      setError("Unable to fetch notes");
    } finally {
      setLoading(false);
    }
  }, [user.uid, userCache]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleShowAddForm = () => {
    setShowAddForm(true);
  };

  const handleAddFormDone = async (newNote) => {
    if (newNote && newNote.createdBy) {
      try {
        await userCache.fetchIds([newNote.createdBy]);
      } catch (err) {
        console.error("Failed to fetch author for new note", err);
      }
      setNotes((prev) => [newNote, ...prev]);
    }
    setShowAddForm(false);
  };

  const handleAddFormCancel = () => {
    setShowAddForm(false);
  };

  return (
    <UserNotesWrapper>
      <UserNotesSectionHeader>
        <h3>Notes</h3>
        {/*
          id is needed here to override styles. There is global button style that targets
          all the buttons in the admin panel and has a specificity of (1, 0, 1) which is high.
          Adding an id here lets us override the style.
        */}
        {
          !showAddForm && !loading && !error && (
            <SudoButton id="add-note" variant="secondary" size="xsmall" onClick={handleShowAddForm}>Add note</SudoButton>
          )
        }
      </UserNotesSectionHeader>
      {
        error ? <ErrorMessage>{error}</ErrorMessage> : (
          <>
            {
              showAddForm && (
                <UserNoteForm onAdd={handleAddFormDone} user={user} onCancel={handleAddFormCancel} />
              )
            }
            {loading && <p className="text-sm text-gray-500">Refreshing notes…</p>}
            <UserNotesList>
              {
                Boolean(notes.length) && (
                  notes.map(note => (
                    <UserNote note={note} key={note.id} author={userCache.getUser(note.createdBy)}/>
                  ))
                )
              }
            </UserNotesList>
          </>
        )
      }
    </UserNotesWrapper>
  );
};

export default UserNotes;
